declare const CF17: {
    ajaxurl: string
    altchaChallengeUrl: string
    uploadAction: string
    minSubmitDelay: number
    i18n: {
        errorSingular: string
        errorPlural: string
        waitForUploads: string
        maxFiles: string
        fileTooLarge: string
        invalidFileType: string
        removeFile: string
        uploadProgress: string
        fieldRequired: string
        uploadRequired: string
        tooEarly: string
    }
}

interface ZoneState {
    token: string
    fileIds: string[]
    pending: number
}

export default class ContactForm {
    protected form: HTMLFormElement
    protected statusRegion: HTMLElement
    protected submitBtn: HTMLButtonElement
    private isSubmitting = false
    private readyAt = 0

    private uploadZones = new Map<HTMLElement, ZoneState>()

    constructor(form: HTMLFormElement) {
        this.form         = form
        this.statusRegion = form.querySelector('[data-form-status]')!
        this.submitBtn    = form.querySelector('[type="submit"]')!
    }

    init(): void {
        if (CF17.minSubmitDelay > 0) {
            this.readyAt = Date.now() + CF17.minSubmitDelay * 1000
        }

        const widget = this.form.querySelector('altcha-widget')
        if (widget) {
            widget.setAttribute('challenge', CF17.altchaChallengeUrl)
            this.waitForAltcha()
        }

        this.initUploadZones()
        this.initInfoModals()

        // Inject upload token + file IDs before submit fires (capture phase)
        this.form.addEventListener('submit', () => this.injectUploadData(), true)
        this.form.addEventListener('submit', (e) => this.onSubmit(e))

        this.form.querySelectorAll<HTMLElement>('[required]').forEach((el) =>
            el.addEventListener('blur', () => this.validateFieldOnBlur(el))
        )
    }

    // ── Altcha load guard ─────────────────────────────────────────────────────

    private waitForAltcha(): void {
        const timeout = new Promise<never>((_, reject) => setTimeout(reject, 8000))
        Promise.race([customElements.whenDefined('altcha-widget'), timeout])
            .catch(() => {
                this.statusRegion.textContent = this.form.dataset.errorMessage
                    ?? 'CAPTCHA could not be loaded. Please reload the page.'
                this.submitBtn.disabled = true
            })
    }

    // ── Upload zones ──────────────────────────────────────────────────────────

    private initUploadZones(): void {
        this.form.querySelectorAll<HTMLElement>('[data-upload-zone]').forEach((zone) => {
            this.uploadZones.set(zone, { token: '', fileIds: [], pending: 0 })
            this.initUploadZone(zone)
        })

        if (!this.uploadZones.size) return

        // Single submit listener validates all zones before the main submit handler
        this.form.addEventListener('submit', (e) => {
            for (const [zone, state] of this.uploadZones) {
                const errorSpan = zone.querySelector<HTMLElement>('.cf17-field__error')
                const showError = (msg: string) => { if (errorSpan) errorSpan.textContent = msg }

                if (state.pending > 0) {
                    e.preventDefault()
                    e.stopImmediatePropagation()
                    showError(CF17.i18n.waitForUploads)
                    return
                }
                if (zone.dataset.required && state.fileIds.length === 0) {
                    e.preventDefault()
                    e.stopImmediatePropagation()
                    showError(CF17.i18n.uploadRequired)
                    return
                }
            }
        }, true)
    }

    private initUploadZone(zone: HTMLElement): void {
        const state      = this.uploadZones.get(zone)!
        const fileInput  = zone.querySelector<HTMLInputElement>('.cf17-upload-zone__input')!
        const preview    = zone.querySelector<HTMLElement>('.cf17-upload-zone__preview')!
        const droparea   = zone.querySelector<HTMLElement>('.cf17-upload-zone__droparea')!
        const errorSpan  = zone.querySelector<HTMLSpanElement>('.cf17-field__error')!

        const maxFiles    = parseInt(zone.dataset.maxFiles ?? '5', 10)
        const maxBytes    = parseInt(zone.dataset.maxBytes ?? '10485760', 10)
        const acceptTypes = (zone.dataset.accept ?? '').split(',').map((s) => s.trim()).filter(Boolean)
        const nonce       = zone.dataset.uploadNonce ?? ''

        const showError  = (msg: string) => (errorSpan.textContent = msg)
        const clearError = () => (errorSpan.textContent = '')

        const uploadFile = (file: File): Promise<void> => new Promise((resolve) => {
            clearError()

            if (state.fileIds.length + state.pending >= maxFiles) {
                showError(CF17.i18n.maxFiles.replace('%d', String(maxFiles)))
                resolve()
                return
            }
            if (file.size > maxBytes) {
                showError(CF17.i18n.fileTooLarge.replace('%d', String(Math.round(maxBytes / 1024 / 1024))))
                resolve()
                return
            }
            if (acceptTypes.length && file.type && !acceptTypes.includes(file.type)) {
                showError(CF17.i18n.invalidFileType)
                resolve()
                return
            }

            state.pending++
            const li = this.addPreviewItem(preview, file.name, (fileId) => {
                state.fileIds = state.fileIds.filter((id) => id !== fileId)
            })

            const fd = new FormData()
            fd.append('action',   CF17.uploadAction)
            fd.append('_wpnonce', nonce)
            fd.append('file',     file)
            if (state.token) fd.append('upload_token', state.token)

            const xhr        = new XMLHttpRequest()
            const progressEl = li.querySelector<HTMLProgressElement>('progress')

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && progressEl) progressEl.value = e.loaded / e.total
            })

            xhr.addEventListener('load', () => {
                state.pending--
                if (progressEl) progressEl.remove()
                try {
                    const json = JSON.parse(xhr.responseText)
                    if (json.success) {
                        state.token = json.token
                        state.fileIds.push(json.fileId)
                        li.dataset.fileId = json.fileId
                    } else {
                        li.querySelector<HTMLElement>('.cf17-upload-status')!.textContent = json.message ?? 'Error'
                    }
                } catch {
                    li.querySelector<HTMLElement>('.cf17-upload-status')!.textContent = 'Error'
                }
                resolve()
            })

            xhr.addEventListener('error', () => {
                state.pending--
                if (progressEl) progressEl.remove()
                li.querySelector<HTMLElement>('.cf17-upload-status')!.textContent = 'Network error'
                resolve()
            })

            xhr.open('POST', CF17.ajaxurl)
            xhr.send(fd)
        })

        // Sequential on purpose: parallel uploads caused token collisions between concurrent requests.
        fileInput.addEventListener('change', async () => {
            for (const file of Array.from(fileInput.files ?? [])) await uploadFile(file)
            fileInput.value = ''
        })

        droparea.addEventListener('dragover', (e) => { e.preventDefault(); droparea.classList.add('drag-over') })
        droparea.addEventListener('dragleave', () => droparea.classList.remove('drag-over'))
        droparea.addEventListener('drop', async (e) => {
            e.preventDefault()
            droparea.classList.remove('drag-over')
            for (const file of Array.from(e.dataTransfer?.files ?? [])) await uploadFile(file)
        })
        droparea.addEventListener('click', () => fileInput.click())
        droparea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click() }
        })
    }

    private addPreviewItem(
        list: HTMLElement,
        name: string,
        onRemove: (fileId: string) => void
    ): HTMLLIElement {
        const li = document.createElement('li')
        li.className = 'cf17-upload-zone__item'
        li.innerHTML = `
            <span class="cf17-upload-zone__filename">${this.escHtml(name)}</span>
            <progress class="cf17-upload-zone__progress" value="0" max="1" aria-label="${this.escHtml(CF17.i18n.uploadProgress)}"></progress>
            <span class="cf17-upload-status"></span>
            <button type="button" class="cf17-upload-zone__remove" aria-label="${this.escHtml(CF17.i18n.removeFile)}">&times;</button>
        `
        li.querySelector('.cf17-upload-zone__remove')!.addEventListener('click', () => {
            const fileId = li.dataset.fileId
            if (fileId) onRemove(fileId)
            li.remove()
        })
        list.appendChild(li)
        return li
    }

    private injectUploadData(): void {
        this.uploadZones.forEach((state, zone) => {
            const zoneName = zone.dataset.zoneName ?? 'default'
            const suffix   = zoneName === 'default' ? '' : '_' + zoneName
            const tokenInput = this.form.querySelector<HTMLInputElement>(`[name="upload_token${suffix}"]`)
            const idsInput   = this.form.querySelector<HTMLInputElement>(`[name="upload_file_ids${suffix}"]`)
            if (tokenInput) tokenInput.value = state.token
            if (idsInput)   idsInput.value   = state.fileIds.join(',')
        })
    }

    // ── Info modals ───────────────────────────────────────────────────────────

    private initInfoModals(): void {
        this.form.querySelectorAll<HTMLButtonElement>('.cf17-info-trigger').forEach((btn) => {
            const targetId = btn.getAttribute('aria-controls')
            if (!targetId) return
            const dialog = document.getElementById(targetId) as HTMLDialogElement | null
            if (!dialog) return

            btn.addEventListener('click', () => dialog.showModal())
            dialog.querySelector('.cf17-info-modal__close')?.addEventListener('click', () => dialog.close())
            dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close() })
            dialog.addEventListener('close', () => btn.focus())
        })
    }

    // ── Form submission ───────────────────────────────────────────────────────

    private async onSubmit(e: SubmitEvent): Promise<void> {
        e.preventDefault()

        if (this.readyAt && Date.now() < this.readyAt) {
            const remaining = Math.ceil((this.readyAt - Date.now()) / 1000)
            this.statusRegion.textContent = CF17.i18n.tooEarly.replace('%d', String(remaining))
            return
        }

        if (this.isSubmitting) return

        this.isSubmitting = true
        this.submitBtn.disabled = true
        this.submitBtn.setAttribute('aria-busy', 'true')
        this.clearErrors()

        try {
            const res  = await fetch(CF17.ajaxurl, { method: 'POST', body: new FormData(this.form) })
            const json = await res.json()

            if (json.success) {
                this.replaceWithSuccess()
            } else {
                this.showErrors(json.errors ?? {}, json.message)
                this.isSubmitting = false
                this.submitBtn.disabled = false
                this.submitBtn.setAttribute('aria-busy', 'false')
            }
        } catch {
            this.showGlobalError()
            this.isSubmitting = false
            this.submitBtn.disabled = false
            this.submitBtn.setAttribute('aria-busy', 'false')
        }
    }

    private replaceWithSuccess(): void {
        const el = document.createElement('div')
        el.className = 'cf17-form__success'
        el.setAttribute('role', 'status')
        el.setAttribute('tabindex', '-1')
        el.textContent = this.form.dataset.successMessage ?? 'Your message has been sent.'
        this.form.replaceWith(el)
        el.focus()
    }

    protected showErrors(errors: Record<string, string>, globalMessage?: string): void {
        const iid = this.form.dataset.instanceId ?? ''
        let count = 0
        for (const [field, msg] of Object.entries(errors)) {
            const input   = this.form.querySelector<HTMLElement>(`[name="${field}"]`)
                         ?? this.form.querySelector<HTMLElement>(`[data-field-name="${field}"]`)
            const errorEl = this.form.querySelector<HTMLElement>(`#error-${iid}-${field}`)
            if (input)   { input.setAttribute('aria-invalid', 'true') }
            if (errorEl) errorEl.textContent = msg
            count++
        }

        this.statusRegion.textContent = count > 0
            ? (count === 1 ? CF17.i18n.errorSingular : CF17.i18n.errorPlural.replace('%d', String(count)))
            : (globalMessage ?? this.form.dataset.errorMessage ?? 'An error occurred.')

        this.form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
    }

    private showGlobalError(): void {
        this.statusRegion.textContent = this.form.dataset.errorMessage ?? 'An error occurred. Please try again later.'
        this.statusRegion.focus()
    }

    private clearErrors(): void {
        this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'))
        this.form.querySelectorAll('[id^="error-"]').forEach((el) => (el.textContent = ''))
        this.statusRegion.textContent = ''
    }

    private validateFieldOnBlur(el: HTMLElement): void {
        const input   = el as HTMLInputElement
        if (input.type === 'radio' || input.type === 'checkbox') return
        const iid     = this.form.dataset.instanceId ?? ''
        const errorEl = this.form.querySelector<HTMLElement>(`#error-${iid}-${input.name}`)
        if (!input.value.trim() && input.required) {
            input.setAttribute('aria-invalid', 'true')
            if (errorEl) errorEl.textContent = CF17.i18n.fieldRequired
        } else {
            input.removeAttribute('aria-invalid')
            if (errorEl) errorEl.textContent = ''
        }
    }

    protected escHtml(str: string): string {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
    }
}

// Auto-init for lazy mode: initialises all .cf17-form elements automatically.
// Full-control mode forms use a custom class and are initialised manually.
const autoInit = () =>
    document.querySelectorAll<HTMLFormElement>('.cf17-form').forEach((el) => new ContactForm(el).init())

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', autoInit)
    : autoInit()
